/**
 * Fluxo Financeiro Sankey Chart
 *
 * Custom SVG Sankey diagram showing the money flow at Monumenta:
 *   Col 0 (Clientes): Top N clients + "Outros"
 *   Col 1 (Receitas): Receita Bruta + Receita Financeira
 *   Col 2 (Destino):  Folha, Terceiros, Despesas, Impostos, Lucro Líquido
 *
 * Uses a GLOBAL scale so node heights are proportional across all columns.
 * Pure SVG — no external charting library required.
 */

import { useMemo, useState } from 'react'
import { Eye, EyeOff, SlidersHorizontal, Workflow } from 'lucide-react'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PositionedNode {
  id: string
  label: string
  value: number
  color: string
  col: number
  x: number
  y: number
  h: number
  labelY: number
}

export interface ClientEntry {
  clientName: string
  receitas: number
}

export interface FluxoFinanceiroSankeyProps {
  faturamento: number
  receitaBruta: number
  receitaFinanceira: number
  folha: number
  terceiros: number
  despesas: number
  impostos: number
  lucroLiquido: number
  /** Client-level revenue breakdown (from client matrix) */
  clients?: ClientEntry[]
  /** Adjusted values (optional — enables "Usar valores ajustados" toggle) */
  folhaAjustada?: number
  terceirosAjustado?: number
  lucroAjustado?: number
}

// ---------------------------------------------------------------------------
// Color palette (Monumenta theme)
// ---------------------------------------------------------------------------

// Semantic colors: revenue = gray, cost = red-400, profit = brand-primary
const C_REVENUE = '#9CA3AF'   // gray-400
const C_COST = '#F87171'      // red-400 (app standard)
const C_PROFIT = '#D2FA52'    // brand-primary (Monumenta lime)

/** All client nodes share the revenue color */
const CLIENT_COLOR = C_REVENUE

const DEST_COLORS: Record<string, string> = {
  receitaBruta: C_REVENUE,
  receitaFinanceira: C_REVENUE,
  folha: C_COST,
  terceiros: C_COST,
  despesas: C_COST,
  impostos: C_COST,
  lucro: C_PROFIT,
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MAX_CLIENTS = 8

/** Compact R$ formatter */
function fmtCompact(value: number): string {
  const abs = Math.abs(value)
  if (abs >= 1_000_000)
    return `R$ ${(abs / 1_000_000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}M`
  if (abs >= 1_000)
    return `R$ ${(abs / 1_000).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}K`
  return `R$ ${abs.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
}

/** Build a smooth cubic bezier path between two vertical bands */
function sankeyPath(
  x0: number, y0Top: number, y0Bot: number,
  x1: number, y1Top: number, y1Bot: number,
): string {
  const mx = (x0 + x1) / 2
  return [
    `M ${x0},${y0Top}`,
    `C ${mx},${y0Top} ${mx},${y1Top} ${x1},${y1Top}`,
    `L ${x1},${y1Bot}`,
    `C ${mx},${y1Bot} ${mx},${y0Bot} ${x0},${y0Bot}`,
    'Z',
  ].join(' ')
}

/** Truncate long client names */
function truncate(s: string, max: number): string {
  return s.length > max ? s.slice(0, max - 1) + '…' : s
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FluxoFinanceiroSankey({
  faturamento,
  receitaBruta,
  receitaFinanceira,
  folha,
  terceiros,
  despesas,
  impostos,
  lucroLiquido,
  clients,
  folhaAjustada,
  terceirosAjustado,
  lucroAjustado,
}: FluxoFinanceiroSankeyProps) {
  const [hoveredLink, setHoveredLink] = useState<string | null>(null)
  const [hoveredNode, setHoveredNode] = useState<string | null>(null)
  const [showLucro, setShowLucro] = useState(true)
  const [showParceria, setShowParceria] = useState(true)
  const [useAdjusted, setUseAdjusted] = useState(false)

  // Whether adjusted values are available at all
  const hasAdjusted = folhaAjustada !== undefined || terceirosAjustado !== undefined || lucroAjustado !== undefined

  // Resolve effective values based on toggle
  const effectiveFolha = useAdjusted && folhaAjustada !== undefined ? folhaAjustada : folha
  const effectiveTerceiros = useAdjusted && terceirosAjustado !== undefined ? terceirosAjustado : terceiros
  const effectiveLucro = useAdjusted && lucroAjustado !== undefined ? lucroAjustado : lucroLiquido

  // Build the sankey data model (4 columns)
  // Col 0: Faturamento por cliente
  // Col 1: Receita por cliente
  // Col 2: Receita Bruta + Rec. Financeira
  // Col 3: Custos + Lucro
  const model = useMemo(() => {
    const fat = Math.abs(faturamento)
    const rec = Math.abs(receitaBruta)
    const recFin = Math.abs(receitaFinanceira)
    const fol = Math.abs(effectiveFolha)
    const terc = Math.abs(effectiveTerceiros)
    const desp = Math.abs(despesas)
    const imp = Math.abs(impostos)
    const lucro = Math.abs(effectiveLucro)

    // Sort ALL clients first (for stable share calculation)
    const allSorted = (clients ?? [])
      .filter((c) => c.receitas > 0)
      .sort((a, b) => b.receitas - a.receitas)

    // Compute faturamento shares from ALL clients (before filtering)
    const allTop = allSorted.slice(0, MAX_CLIENTS)
    const allOthers = allSorted.slice(MAX_CLIENTS).reduce((s, c) => s + c.receitas, 0)
    const allClientList = allTop.map((c, i) => ({
      key: `client-${i}`,
      label: truncate(c.clientName, 16),
      name: c.clientName,
      receita: c.receitas,
    }))
    if (allOthers > 0) {
      allClientList.push({ key: 'client-others', label: 'Outros', name: 'Outros', receita: allOthers })
    }
    const totalAllReceita = allClientList.reduce((s, c) => s + c.receita, 0)

    // Assign fixed faturamento per client (based on full list)
    const clientsWithFat = allClientList.map((c) => ({
      ...c,
      faturamento: totalAllReceita > 0 ? fat * (c.receita / totalAllReceita) : fat / allClientList.length,
    }))

    // Now filter out Parceria if hidden
    const clientList = showParceria
      ? clientsWithFat
      : clientsWithFat.filter((c) => !c.name.toUpperCase().includes('PARCERIA'))

    if (clientList.length === 0) {
      clientList.push({ key: 'client-all', label: 'Clientes', name: 'Clientes', receita: rec, faturamento: fat })
    }

    // --- Col 0: Faturamento por cliente (fixed values) ---
    const fatNodes = clientList.map((c) => ({
      id: `fat-${c.key}`,
      label: c.label,
      value: c.faturamento,
      color: C_REVENUE,
      col: 0,
    }))

    // --- Col 1: Receita por cliente ---
    const recNodes = clientList.map((c) => ({
      id: `rec-${c.key}`,
      label: c.label,
      value: c.receita,
      color: C_REVENUE,
      col: 1,
    }))

    // --- Col 2: Receita Bruta + Rec. Financeira ---
    const receitaNodes = [
      { id: 'receitaBruta', label: 'Receita Bruta', value: rec, color: C_REVENUE, col: 2 },
      { id: 'receitaFinanceira', label: 'Rec. Financeira', value: recFin, color: C_REVENUE, col: 2 },
    ]

    // --- Col 3: Destino ---
    const adj = useAdjusted
    const costItems = [
      { id: 'folha', label: adj && folhaAjustada !== undefined ? 'Folha Ajust.' : 'Folha', value: fol, color: DEST_COLORS.folha, col: 3 },
      { id: 'terceiros', label: adj && terceirosAjustado !== undefined ? 'Terceiros Ajust.' : 'Terceiros', value: terc, color: DEST_COLORS.terceiros, col: 3 },
      { id: 'despesas', label: 'Despesas', value: desp, color: DEST_COLORS.despesas, col: 3 },
      { id: 'impostos', label: 'Impostos', value: imp, color: DEST_COLORS.impostos, col: 3 },
    ]
    const destNodes = showLucro
      ? [...costItems, { id: 'lucro', label: adj && lucroAjustado !== undefined ? 'Lucro Ajust.' : 'Lucro Líquido', value: lucro, color: DEST_COLORS.lucro, col: 3 }]
      : costItems

    // --- Links Col0 → Col1: Faturamento cliente → Receita cliente ---
    const linksF2R = clientList.map((c) => ({
      source: `fat-${c.key}`,
      target: `rec-${c.key}`,
      value: c.receita, // link sized by the smaller value (receita)
      color: C_REVENUE,
    }))

    // --- Links Col1 → Col2: Receita cliente → Receita Bruta ---
    const linksR2Agg = clientList.map((c) => ({
      source: `rec-${c.key}`,
      target: 'receitaBruta',
      value: c.receita,
      color: C_REVENUE,
    }))

    // --- Links Col2 → Col3: Receitas → Destino ---
    const totalInflow = rec + recFin
    const recShare = totalInflow > 0 ? rec / totalInflow : 0.9
    const recFinShare = totalInflow > 0 ? recFin / totalInflow : 0.1

    const linksAgg2D: { source: string; target: string; value: number; color: string }[] = []
    for (const item of destNodes) {
      linksAgg2D.push({
        source: 'receitaBruta',
        target: item.id,
        value: item.value * recShare,
        color: item.color,
      })
      linksAgg2D.push({
        source: 'receitaFinanceira',
        target: item.id,
        value: item.value * recFinShare,
        color: item.color,
      })
    }

    const columns = [fatNodes, recNodes, receitaNodes, destNodes]
    const allNodes = columns.flat()
    const allLinks = [...linksF2R, ...linksR2Agg, ...linksAgg2D]

    return { columns, allNodes, links: allLinks }
  }, [faturamento, receitaBruta, receitaFinanceira, effectiveFolha, effectiveTerceiros, despesas, impostos, effectiveLucro, clients, showLucro, showParceria, useAdjusted, folhaAjustada, terceirosAjustado, lucroAjustado])

  // ---------------------------------------------------------------------------
  // Layout computation — GLOBAL scale
  // ---------------------------------------------------------------------------

  const layout = useMemo(() => {
    const W = 1200
    const H = 560
    const PAD_TOP = 10
    const PAD_BOT = 24
    const NODE_W = 14
    // 4 columns: Faturamento/cliente | Receita/cliente | Receitas | Destino
    const COL_X = [140, 380, 700, W - 130 - NODE_W]

    const usableH = H - PAD_TOP - PAD_BOT

    const { columns } = model
    const colTotals = columns.map((col) => col.reduce((s, n) => s + n.value, 0))

    const maxTotal = Math.max(...colTotals)
    if (maxTotal === 0) return { positioned: [] as PositionedNode[], linkPaths: [] as any[], colHeaders: [] as { x: number; label: string }[], W, H }

    // Pure proportional scale — no min height
    const GAP = 6
    let pxPerUnit = 0
    for (let i = 0; i < columns.length; i++) {
      if (colTotals[i] <= 0) continue
      const totalGaps = GAP * Math.max(0, columns[i].length - 1)
      const available = usableH - totalGaps
      if (available <= 0) continue
      const candidate = available / colTotals[i]
      if (pxPerUnit === 0 || candidate < pxPerUnit) {
        pxPerUnit = candidate
      }
    }

    function layoutColumn(nodes: typeof columns[0], colIdx: number): PositionedNode[] {
      const totalGap = GAP * Math.max(0, nodes.length - 1)
      const colHeight = colTotals[nodes[0]?.col ?? 0] * pxPerUnit + totalGap
      const startY = PAD_TOP + (usableH - colHeight) / 2

      let y = startY
      return nodes.map((n) => {
        const h = Math.max(n.value * pxPerUnit, 2)
        const mid = y + h / 2
        const node: PositionedNode = { ...n, x: COL_X[n.col], y, h, labelY: mid }
        y += h + GAP
        return node
      })
    }

    const positioned = columns.flatMap((col, i) => layoutColumn(col, i))
    const nodeMap = new Map(positioned.map((n) => [n.id, n]))

    // Compute link paths
    const sourceOffsets = new Map<string, number>()
    const targetOffsets = new Map<string, number>()

    const linkPaths = model.links.map((link) => {
      const src = nodeMap.get(link.source)
      const tgt = nodeMap.get(link.target)
      if (!src || !tgt) return null

      const srcTotal = model.links.filter((l) => l.source === link.source).reduce((s, l) => s + l.value, 0)
      const tgtTotal = model.links.filter((l) => l.target === link.target).reduce((s, l) => s + l.value, 0)

      const linkH_src = srcTotal > 0 ? (link.value / srcTotal) * src.h : 0
      const linkH_tgt = tgtTotal > 0 ? (link.value / tgtTotal) * tgt.h : 0

      const srcOff = sourceOffsets.get(link.source) ?? 0
      const tgtOff = targetOffsets.get(link.target) ?? 0

      const x0 = src.x + NODE_W
      const y0Top = src.y + srcOff
      const y0Bot = y0Top + linkH_src
      const x1 = tgt.x
      const y1Top = tgt.y + tgtOff
      const y1Bot = y1Top + linkH_tgt

      sourceOffsets.set(link.source, srcOff + linkH_src)
      targetOffsets.set(link.target, tgtOff + linkH_tgt)

      return {
        key: `${link.source}-${link.target}`,
        d: sankeyPath(x0, y0Top, y0Bot, x1, y1Top, y1Bot),
        color: link.color,
        value: link.value,
        source: link.source,
        target: link.target,
      }
    }).filter(Boolean) as {
      key: string; d: string; color: string; value: number; source: string; target: string
    }[]

    // Column header positions (above nodes)
    const colHeaders = [
      { x: COL_X[0] + NODE_W / 2, label: 'Faturamento' },
      { x: COL_X[1] + NODE_W / 2, label: 'Receita' },
      { x: COL_X[2] + NODE_W / 2, label: 'Consolidado' },
      { x: COL_X[3] + NODE_W / 2, label: 'Destino' },
    ]

    return { positioned, linkPaths, colHeaders, W, H }
  }, [model])

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-brand-gray flex items-center gap-2">
          <Workflow size={20} strokeWidth={1.5} />
          Fluxo Financeiro
        </h3>
        <div className="flex items-center gap-3">
          {hasAdjusted && (
            <button
              onClick={() => setUseAdjusted((v) => !v)}
              className={`flex items-center gap-1.5 text-[11px] transition-colors ${useAdjusted ? 'text-brand-primary hover:text-brand-primary/80' : 'text-brand-gray/50 hover:text-brand-gray'}`}
            >
              <SlidersHorizontal className="w-3.5 h-3.5" />
              Valores ajustados
            </button>
          )}
          <button
            onClick={() => setShowParceria((v) => !v)}
            className={`flex items-center gap-1.5 text-[11px] transition-colors ${showParceria ? 'text-brand-gray hover:text-white' : 'text-brand-gray/50 hover:text-brand-gray'}`}
          >
            {showParceria ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            Parceria
          </button>
          <button
            onClick={() => setShowLucro((v) => !v)}
            className={`flex items-center gap-1.5 text-[11px] transition-colors ${showLucro ? 'text-brand-gray hover:text-white' : 'text-brand-gray/50 hover:text-brand-gray'}`}
          >
            {showLucro ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            Lucro
          </button>
        </div>
      </div>
        <svg
          viewBox={`0 0 ${layout.W} ${layout.H}`}
          className="w-full h-auto"
          preserveAspectRatio="xMidYMid meet"
        >
          {/* Column headers */}
          {layout.colHeaders.map((ch) => (
            <text
              key={ch.label}
              x={ch.x}
              y={layout.H - 4}
              textAnchor="middle"
              fill="#838998"
              fontSize={10}
              fontWeight={600}
              fontFamily="Inter, system-ui, sans-serif"
              letterSpacing="0.5"
              textDecoration="none"
              style={{ textTransform: 'uppercase' }}
            >
              {ch.label}
            </text>
          ))}

          {/* Links */}
          {layout.linkPaths.map((lp) => {
            const isHovered =
              hoveredLink === lp.key ||
              hoveredNode === lp.source ||
              hoveredNode === lp.target
            const isOther = (hoveredLink !== null || hoveredNode !== null) && !isHovered
            return (
              <path
                key={lp.key}
                d={lp.d}
                fill={lp.color}
                fillOpacity={isOther ? 0.04 : isHovered ? 0.45 : 0.18}
                stroke={lp.color}
                strokeOpacity={isOther ? 0.03 : isHovered ? 0.6 : 0.25}
                strokeWidth={0.5}
                style={{ transition: 'fill-opacity 0.2s, stroke-opacity 0.2s' }}
                onMouseEnter={() => setHoveredLink(lp.key)}
                onMouseLeave={() => setHoveredLink(null)}
              />
            )
          })}

          {/* Nodes + labels */}
          {layout.positioned.map((n) => {
            const isHovered = hoveredNode === n.id
            return (
              <g
                key={n.id}
                onMouseEnter={() => setHoveredNode(n.id)}
                onMouseLeave={() => setHoveredNode(null)}
                className="cursor-pointer"
              >
                <rect
                  x={n.x}
                  y={n.y}
                  width={14}
                  height={Math.max(n.h, 2)}
                  rx={2}
                  fill={n.color}
                  fillOpacity={isHovered ? 1 : 0.85}
                  style={{ transition: 'fill-opacity 0.2s' }}
                />

                {/* Col 0: Faturamento por cliente — name (white) + value (gray) same line */}
                {n.col === 0 && (
                  <text
                    x={n.x - 6}
                    y={n.labelY}
                    textAnchor="end"
                    dominantBaseline="central"
                    fontSize={10}
                    fontFamily="Inter, system-ui, sans-serif"
                  >
                    <tspan fill="#FFFFFF" fontWeight={500}>{n.label}</tspan>
                    <tspan fill="#838998" fontSize={9}>{`  ${fmtCompact(n.value)}`}</tspan>
                  </text>
                )}

                {/* Col 1: Receita por cliente — name (white) + value (gray) same line */}
                {n.col === 1 && (
                  <text
                    x={n.x + 22}
                    y={n.labelY}
                    textAnchor="start"
                    dominantBaseline="central"
                    fontSize={10}
                    fontFamily="Inter, system-ui, sans-serif"
                  >
                    <tspan fill="#FFFFFF" fontWeight={500}>{n.label}</tspan>
                    <tspan fill="#838998" fontSize={9}>{`  ${fmtCompact(n.value)}`}</tspan>
                  </text>
                )}

                {/* Col 2: Receitas — label above */}
                {n.col === 2 && (
                  <>
                    <text
                      x={n.x + 7}
                      y={n.y - 14}
                      textAnchor="middle"
                      fill="#FFFFFF"
                      fontSize={11}
                      fontWeight={600}
                      fontFamily="Inter, system-ui, sans-serif"
                    >
                      {n.label}
                    </text>
                    <text
                      x={n.x + 7}
                      y={n.y - 3}
                      textAnchor="middle"
                      fill="#838998"
                      fontSize={10}
                      fontFamily="Inter, system-ui, sans-serif"
                    >
                      {fmtCompact(n.value)}
                    </text>
                  </>
                )}

                {/* Col 3: Destino — name (white) + value (gray) same line */}
                {n.col === 3 && (
                  <text
                    x={n.x + 22}
                    y={n.labelY}
                    textAnchor="start"
                    dominantBaseline="central"
                    fontSize={11}
                    fontFamily="Inter, system-ui, sans-serif"
                  >
                    <tspan fill="#FFFFFF" fontWeight={600}>{n.label}</tspan>
                    <tspan fill="#838998" fontSize={10}>{`  ${fmtCompact(n.value)}`}</tspan>
                  </text>
                )}
              </g>
            )
          })}
        </svg>
    </div>
  )
}
